import { useState } from 'react';
import { Alert as MuiAlert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { Add, ReceiptLong } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { ProductRouteGuard } from '@/components/ProductRouteGuard';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as cuitProfilesApi from '@/api/cuitProfiles';
import * as afipApi from '@/api/afipIntegrations';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { safeHttpsUrl } from '@/lib/safeExternalUrl';

export const Route = createFileRoute('/_authenticated/vep/')({ component: VepRoute });


function VepRoute(): JSX.Element {
  return (
    <ProductRouteGuard
      products={['Estudio', 'Empresa']}
      requirePermission="canManageAfip"
      title="Generación VEP no disponible"
      description="Seleccioná un producto fiscal con permisos ARCA para generar o consultar VEPs."
    >
      <VepPage />
    </ProductRouteGuard>
  );
}

function VepPage(): JSX.Element {
  const profilesQ = useQuery({ queryKey: ['cuit-profiles', 'vep'], queryFn: () => cuitProfilesApi.listCuitProfiles(0, 200) });
  const [form, setForm] = useState<afipApi.VepCreateInput>({
    cuitProfileId: '', ownerTransactionId: '', nroFormulario: '', codTipoPago: '', importe: '', periodoFiscal: '', entidadDePago: 0,
  });
  const createMut = useMutation({ mutationFn: () => afipApi.createVep({ ...form, periodoFiscal: form.periodoFiscal || null }) });

  if (profilesQ.isLoading) return <LoadingScreen message="Cargando contribuyentes…" />;
  const profiles = profilesQ.data?.items ?? [];
  const set = (key: keyof afipApi.VepCreateInput, value: string | number): void => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ReceiptLong color="primary" sx={{ fontSize: 38 }} />
          <Box>
            <Typography variant="h4">Generación VEP</Typography>
            <Typography variant="body2" color="text.secondary">Generá y consultá VEPs desde la API oficial SETIWS-PAGO-API.</Typography>
          </Box>
        </Stack>
        <MuiAlert severity="warning">
          ARCA habilita esta API únicamente a organismos con convenio de intercambio y JWT de WSAUTH. La pantalla no genera VEPs simulados: si Tilde no tiene esa habilitación configurada, la operación devuelve un mensaje claro.
        </MuiAlert>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <TextField select label="Contribuyente" value={form.cuitProfileId} onChange={(e) => set('cuitProfileId', e.target.value)} fullWidth>
              <MenuItem value="">Seleccionar CUIT</MenuItem>
              {profiles.map((profile) => <MenuItem key={profile.id} value={profile.id}>{profile.razonSocial} · {profile.cuit}</MenuItem>)}
            </TextField>
            <TextField label="ID de transacción" helperText="Debe ser único por VEP; permite reintentar sin duplicar." value={form.ownerTransactionId} onChange={(e) => set('ownerTransactionId', e.target.value)} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Formulario" value={form.nroFormulario} onChange={(e) => set('nroFormulario', e.target.value)} fullWidth />
              <TextField label="Código tipo de pago" value={form.codTipoPago} onChange={(e) => set('codTipoPago', e.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Importe" value={form.importe} onChange={(e) => set('importe', e.target.value)} fullWidth inputMode="decimal" />
              <TextField label="Período fiscal (YYYYMM)" value={form.periodoFiscal ?? ''} onChange={(e) => set('periodoFiscal', e.target.value)} fullWidth />
              <TextField select label="Entidad de pago" value={form.entidadDePago} onChange={(e) => set('entidadDePago', Number(e.target.value))} fullWidth>
                <MenuItem value={0}>QR</MenuItem><MenuItem value={1001}>Red Link</MenuItem><MenuItem value={1002}>Banelco</MenuItem><MenuItem value={1003}>Interbanking</MenuItem>
              </TextField>
            </Stack>
            <Button
              variant="contained"
              startIcon={createMut.isPending ? <CircularProgress size={18} color="inherit" /> : <Add />}
              disabled={!form.cuitProfileId || !form.ownerTransactionId || !form.nroFormulario || !form.codTipoPago || !form.importe || !form.periodoFiscal || createMut.isPending}
              onClick={() => createMut.mutate()}
            >Generar VEP en ARCA</Button>
            {createMut.error ? <MuiAlert severity="error">{toApiError(createMut.error).message}</MuiAlert> : null}
            {createMut.data ? (
              <MuiAlert severity="success">
                VEP generado: <strong>{createMut.data.nroVep}</strong>.
                {safeHttpsUrl(createMut.data.entidadDePagoUrl) ? <Button component="a" href={safeHttpsUrl(createMut.data.entidadDePagoUrl)!} target="_blank" rel="noreferrer" size="small">Abrir entidad de pago</Button> : null}
                {createMut.data.qrBase64 ? <Box component="img" src={createMut.data.qrBase64} alt="QR del VEP" sx={{ display: 'block', width: 220, mt: 2 }} /> : null}
              </MuiAlert>
            ) : null}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
